import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../firebase'
import { wikimediaThumb } from '../data/parcoursIllustrations'
import { isValidAvatarSuggestionId } from '../data/avatarCommonsSearchSuggestions'

const DOC_PATH = 'appSettings'
const DOC_ID = 'avatarPresets'
const DOC_REF = doc(db, DOC_PATH, DOC_ID)

const COMMONS_PREFIX = 'https://upload.wikimedia.org/wikipedia/commons/'
const DISPLAY_THUMB_W = 160
/** Largeur stockée à l’ajout — 500px aligné sur les aperçus Commons courants (480px absent pour certains fichiers). */
const STORAGE_THUMB_W = 500

/**
 * URL autorisée pour un avatar preset (fichier ou variante /thumb/ sur Commons).
 * @param {string} url
 * @returns {boolean}
 */
export function isAllowedCommonsAvatarUrl(url) {
  if (!url || typeof url !== 'string') return false
  const t = url.trim()
  if (!t.startsWith(COMMONS_PREFIX)) return false
  if (t.includes('..')) return false
  if (!/^https:\/\/upload\.wikimedia\.org\/wikipedia\/commons\//i.test(t)) return false
  return true
}

/**
 * Normalise pour dédoublonnage (trim, sans fragment).
 * @param {string} url
 */
export function normalizePresetUrl(url) {
  if (!url || typeof url !== 'string') return ''
  try {
    const u = new URL(url.trim())
    u.hash = ''
    return u.href
  } catch {
    return url.trim()
  }
}

/**
 * Réécrit une URL fichier Commons en miniature /thumb/ si ce n’en est pas déjà une.
 * @param {string} trimmedUrl
 * @returns {string}
 */
export function normalizeCommonsAvatarStorageUrl(trimmedUrl) {
  if (!trimmedUrl || typeof trimmedUrl !== 'string') return trimmedUrl
  if (trimmedUrl.includes('/thumb/')) return trimmedUrl
  return wikimediaThumb(trimmedUrl, STORAGE_THUMB_W)
}

/**
 * @typedef {{ id: string, url: string, thumbUrl: string, label?: string, category?: string, source: 'firestore' }} MergedAvatarPreset
 */

/**
 * @param {{ id: string, url: string, suggestionId?: string }[]} extraFromFirestore
 * @returns {MergedAvatarPreset[]}
 */
export function mergeAvatarPresets(extraFromFirestore = []) {
  const seen = new Set()
  /** @type {MergedAvatarPreset[]} */
  const out = []

  for (const row of extraFromFirestore) {
    if (!row?.url || !row?.id) continue
    const key = normalizePresetUrl(row.url)
    if (seen.has(key)) continue
    seen.add(key)
    out.push({
      id: row.id,
      url: row.url,
      thumbUrl: wikimediaThumb(row.url, DISPLAY_THUMB_W),
      label: row.label,
      category: row.category,
      source: 'firestore'
    })
  }

  return out
}

/**
 * @returns {Promise<{ id: string, url: string, suggestionId?: string }[]>}
 */
export async function getAvatarPresetsExtra() {
  const snap = await getDoc(DOC_REF)
  if (!snap.exists()) return []
  const entries = snap.data()?.entries
  return Array.isArray(entries) ? entries.filter((e) => e?.id && e?.url) : []
}

/** Empreinte stable des entrées Firestore (évite setState + re-render grille à chaque snapshot identique). */
function serializeAvatarExtraEntries(entries) {
  const rows = (Array.isArray(entries) ? entries : [])
    .filter((e) => e?.id && e?.url)
    .map((e) => ({
      id: String(e.id),
      url: normalizePresetUrl(e.url),
      suggestionId: e.suggestionId != null && e.suggestionId !== '' ? String(e.suggestionId) : ''
    }))
    .sort((a, b) => a.id.localeCompare(b.id))
  return JSON.stringify(rows)
}

/**
 * @param {(extra: { id: string, url: string, suggestionId?: string }[]) => void} onData
 * @param {(err: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeAvatarPresetsExtra(onData, onError) {
  let lastSerialized = ''
  return onSnapshot(
    DOC_REF,
    (snap) => {
      let entries = []
      if (snap.exists()) {
        const raw = snap.data()?.entries
        entries = Array.isArray(raw) ? raw.filter((e) => e?.id && e?.url) : []
      }
      const serialized = serializeAvatarExtraEntries(entries)
      if (serialized === lastSerialized) return
      lastSerialized = serialized
      onData(entries)
    },
    (err) => {
      console.error('subscribeAvatarPresetsExtra:', err)
      lastSerialized = ''
      onData([])
      onError?.(err)
    }
  )
}

/**
 * @param {string} url
 * @param {{ suggestionId?: string | null }} [options]
 * @returns {Promise<void>}
 */
export async function addAvatarPresetUrl(url, options = {}) {
  const trimmed = (url || '').trim()
  if (!isAllowedCommonsAvatarUrl(trimmed)) {
    throw new Error(
      'URL non valide. Utilisez un lien HTTPS vers un fichier sur upload.wikimedia.org (Wikimedia Commons), pas la page du fichier.'
    )
  }
  const urlToStore = normalizeCommonsAvatarStorageUrl(trimmed)
  const normalized = normalizePresetUrl(urlToStore)
  const snap = await getDoc(DOC_REF)
  const prev = snap.exists() && Array.isArray(snap.data()?.entries) ? [...snap.data().entries] : []
  if (prev.some((e) => normalizePresetUrl(e.url) === normalized)) {
    throw new Error('Cette image est déjà dans la liste.')
  }
  const rawSid = options?.suggestionId
  const suggestionId =
    rawSid != null && String(rawSid).trim() !== '' && isValidAvatarSuggestionId(String(rawSid).trim())
      ? String(rawSid).trim()
      : undefined

  const entry = { id: crypto.randomUUID(), url: urlToStore }
  if (suggestionId) entry.suggestionId = suggestionId
  const next = [...prev, entry]

  if (!snap.exists()) {
    await setDoc(DOC_REF, {
      entries: next,
      updatedAt: serverTimestamp()
    })
  } else {
    await updateDoc(DOC_REF, {
      entries: next,
      updatedAt: serverTimestamp()
    })
  }
}

/**
 * @param {string} entryId
 * @returns {Promise<void>}
 */
export async function removeAvatarPresetById(entryId) {
  if (!entryId || typeof entryId !== 'string') return
  const snap = await getDoc(DOC_REF)
  if (!snap.exists()) return
  const prev = Array.isArray(snap.data()?.entries) ? snap.data().entries : []
  const next = prev.filter((e) => e?.id !== entryId)
  if (next.length === prev.length) return
  await updateDoc(DOC_REF, {
    entries: next,
    updatedAt: serverTimestamp()
  })
}
